import {
  createRouter,
  createWebHashHistory,
  createWebHistory,
  type Router,
  type RouteRecordRaw,
  type RouterHistory,
  type RouteLocationRaw,
} from 'vue-router';

import {ROUTER_BASE_URL, LANGUAGE_REGEX} from '@/ts/utils/consts';
import {extractLanguageFromPath, detectUserLanguage} from '@/ts/composables/stateful/use-i18n-router';
import type {Language} from '@/ts/types/component/localization.type';
import {SUPPORTED_LANGUAGES} from '@/ts/composables/stateful/use-lang';
import {
  DEFAULT_UNAUTHENTICATED_REDIRECT,
  setUnauthenticatedRedirect,
} from '@/ts/router/unauthenticated-redirect';
import {currentLanguage} from '@/ts/instances/i18n';
import {persistentStorage} from '@/ts/instances/persistant-storage';
import {sessionStorage} from '@/ts/instances/session-storage';

function prefixSlash(value: string | null | undefined): string {
  const safe = value ?? '';
  return (safe?.startsWith('/') ?? false) ? safe : `/${safe}`;
}

/**
 * Resolves child paths against parent to create absolute paths
 */
export function resolveAbsolutePath(parentPath: string, childPath: string): string {
  const child = childPath || '/';
  if (child === '/') {
    return parentPath || '/';
  }
  if (String(child).startsWith('/')) {
    return child;
  } // already absolute (keep as-is)

  const base = parentPath && parentPath !== '/' ? parentPath : '';
  return `${base}/${child}`;
}


/**
 * Normalizes the leading language prefix(es) of a path.
 * Handles:
 *  - multiple valid languages: /en/en/explore, /en/en/de/explore -> /en/explore
 *  - invalid language: /fr/explore -> /<userLang>/explore
 *  - missing language: /explore -> /<userLang>/explore
 *  - single valid language: /en/explore -> unchanged
 */
export function normalizeLangInPath(fullPath: string): string {
  const supportedPattern = SUPPORTED_LANGUAGES.join('|');
  const multiValid = new RegExp(`^(?:/(?:${supportedPattern})(?=/|$))+`, 'u');
  const firstValid = new RegExp(`^/(${supportedPattern})(?:/|$)`, 'u');
  const startsWithAnyLang = LANGUAGE_REGEX.STARTS_WITH_LANG;
  const removeOneLang = LANGUAGE_REGEX.REMOVE_LANG_PREFIX;

  // collapse multiple consecutive valid languages to the first one
  const run = fullPath?.match(multiValid)?.[0];
  if (run) {
    const first = firstValid.exec(run)?.[1] as Language | undefined;
    const tail = fullPath?.slice(run?.length ?? 0) ?? '';
    const normalizedTail = tail ? prefixSlash(tail) : '';
    return `/${first!}${normalizedTail}`;
  }

  // if it starts with any 2-letter but NOT supported, replace with user language
  if (startsWithAnyLang.test(fullPath)) {
    const langInUrl = (LANGUAGE_REGEX.PATH_WITH_LANG.exec(fullPath)?.groups?.lang ?? '') as Language | '';
    if (!SUPPORTED_LANGUAGES.includes(langInUrl as Language)) {
      const pathWithout = (fullPath?.replace(removeOneLang, '') ?? '') || '/';
      const userLang = detectUserLanguage();
      return `/${userLang}${pathWithout === '/' ? '' : pathWithout}`;
    }
    // single valid language, keep as is
    return fullPath;
  }

  // no language → add user's language
  const userLang = detectUserLanguage();
  return `/${userLang}${fullPath === '/' ? '' : fullPath}`;
}

/**
 * Transforms route tree to use language parameters (:lang) instead of aliases
 * Each route gets prefixed with /:lang(<supported|joined>) pattern.
 */
export function cloneTreeWithLangPrefix(routes: RouteRecordRaw[]): RouteRecordRaw[] {
  const langPattern = SUPPORTED_LANGUAGES.join('|');
  const PREFIX = `/:lang(${langPattern})`;

  const walk = (route: RouteRecordRaw, parentResolved: string): RouteRecordRaw => {
    const srcPath = route.path || '/';
    const absolute = resolveAbsolutePath(parentResolved, srcPath);
    const prefixed = absolute === '/' ? PREFIX : `${PREFIX}${absolute}`;

    const {alias: _alias, path: _path, children, name, component, components, redirect, meta, props, beforeEnter, ...rest} = route;

    const clone: RouteRecordRaw = {
      path: prefixed,
      ...(component && {component}),
      ...(components && {components}),
      ...(redirect && {redirect}),
      ...(meta && {meta}),
      ...(props && {props}),
      ...(beforeEnter && {beforeEnter}),
      ...(name && {name}),
      ...rest,
    } as RouteRecordRaw;

    if (Array.isArray(children) && children.length) {
      clone.children = children.map((ch: RouteRecordRaw) => walk(ch, absolute));
    }

    return clone;
  };

  return routes.map((r) => walk(r, ''));
}


/**
 * Forces a page reload to the correct language URL for authenticated users.
 * This bypasses bfcache and ensures content is fetched in the correct language.
 */
export function handleLanguageMismatchReload(
  storedLang: Language,
  currentPath: string,
  query: Record<string, unknown>,
  hash: string,
): void {
  const pathWithoutLang = (currentPath?.replace(LANGUAGE_REGEX.REMOVE_LANG_PREFIX, '') ?? '') || '/';
  const correctPath = `/${storedLang}${pathWithoutLang === '/' ? '' : pathWithoutLang}`;
  const queryString = Object.keys(query).length > 0
    ? `?${new URLSearchParams(query as Record<string, string>).toString()}`
    : '';
  window.location.replace(`${window.location.origin}${correctPath}${queryString}${hash}`);
}

/** Options for creating an i18n-aware Vue Router with language prefixes and auth guards */
export interface I18nRouterOptions {
  routes: RouteRecordRaw[];
  unauthenticatedRedirect?: string;
}

/**
 * Creates a Vue Router with i18n support (language prefixes, normalization, guards)
 */
export function createI18nRouter(options: I18nRouterOptions): Router {
  const {
    routes: baseRoutes,
    unauthenticatedRedirect = DEFAULT_UNAUTHENTICATED_REDIRECT,
  } = options;

  const history: RouterHistory = ROUTER_BASE_URL
    ? createWebHashHistory(ROUTER_BASE_URL)
    : createWebHistory();

  const localizedRoutes = cloneTreeWithLangPrefix(baseRoutes);

  const routes: RouteRecordRaw[] = [
    ...localizedRoutes,

    {
      path: '/',
      redirect: () => `/${detectUserLanguage()}`,
    },

    {
      path: '/:pathMatch(.*)*',
      redirect: (to): RouteLocationRaw => {
        const normalized = normalizeLangInPath(to.fullPath);
        // Redirecting to the incoming path would point this record at itself, and vue-router
        // follows a redirect chain until the stack gives out. Getting here with the prefix already
        // in place means nothing matched at all, so hand over the app root — which both apps have,
        // and which the auth guard below carries on to the login page for a session-less visitor.
        return normalized === to.fullPath
          ? {path: `/${detectUserLanguage()}`, replace: true}
          : {path: normalized, replace: true};
      },
    },
  ];

  const router = createRouter({history, routes});
  setUnauthenticatedRedirect(router, unauthenticatedRedirect);

  router.beforeEach((to, _from, next) => {
    // normalize the incoming path first (handles multiple/invalid/missing)
    const normalized = normalizeLangInPath(to.path);
    if (normalized !== to.path) {
      next({
        path: normalized,
        query: to.query,
        hash: to.hash,
        replace: true,
      });
      return;
    }

    // auth check
    if (to.matched[0]?.meta?.auth && !persistentStorage.auth?.access_token) {
      sessionStorage.redirectIntent = to.fullPath;
      next(unauthenticatedRedirect);
      return;
    }

    // Handle language sync between URL and user preference
    const urlLang = (to.params.lang as string | undefined) ?? extractLanguageFromPath(to.path);
    const storedLang = persistentStorage.language;
    const isAuthenticated = Boolean(persistentStorage.auth?.access_token);

    if (urlLang && SUPPORTED_LANGUAGES.includes(urlLang as Language)) {
      // For authenticated users: force reload if URL language differs from stored preference
      if (isAuthenticated && storedLang && storedLang !== (urlLang as Language)) {
        handleLanguageMismatchReload(storedLang, to.path, to.query, to.hash);
        return;
      }

      // For public pages or matching language: sync URL language to storage
      if (storedLang !== (urlLang as Language)) {
        persistentStorage.language = urlLang as Language;
      }
      next();
      return;
    }

    // fallback (shouldn't be hit thanks to normalizeLangInPath)
    const userLang = detectUserLanguage();
    next({
      path: `/${userLang}${to.path === '/' ? '' : to.path}`,
      query: to.query,
      hash: to.hash,
      replace: true,
    });
  });

  // Both entry HTMLs declare the deployed default language, but either app can render in the other
  // one — the locale comes from the URL prefix, stored preference or browser. Here rather than in a
  // factory because it is true of both apps; the tab title is not, and stays in routerFactory.
  router.afterEach(() => {
    document.documentElement.lang = currentLanguage();
  });

  return router;
}
